import { Box, CardContent, Typography } from '@mui/material';
import { AlertTriangle, Package, ShoppingCart } from 'lucide-react';
import { formatRelativeTime } from '../../Services/FormatRelativeTime';
import { COLORS } from '../../Styles/Colors';
import {
  VendingInventorySlot,
  VendingMachineDetail,
  VendingTransactionSummary,
} from '../../Types/vending';
import { ConnectionStatusChip, MachineStatusChip } from './MachineStatusChip';
import {
  ActivityItem,
  VendingDetailLabel,
  VendingDetailRow,
  VendingDetailValue,
  VendingKpiCard,
  VendingSectionCard,
  VendingSectionTitle,
} from './VendingStyles';

interface KpiCardProps {
  label: string;
  value: string | number;
  sublabel?: string;
  icon: React.ReactNode;
  accent?: string;
}

const KpiCard = ({ label, value, sublabel, icon, accent = COLORS.teal }: KpiCardProps) => (
  <VendingKpiCard>
    <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Typography
          variant="body2"
          sx={{ color: COLORS.lightGray, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}
        >
          {label}
        </Typography>
        <Box sx={{ color: accent, opacity: 0.9 }}>{icon}</Box>
      </Box>
      <Typography variant="h3" fontWeight="bold" sx={{ color: accent, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sublabel && (
        <Typography variant="body2" sx={{ mt: 0.75, color: COLORS.lightGray }}>
          {sublabel}
        </Typography>
      )}
    </CardContent>
  </VendingKpiCard>
);

const formatActivityTime = (dateString: string) => {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

interface MachineOverviewTabProps {
  machine: VendingMachineDetail;
  slots: VendingInventorySlot[];
  transactions: VendingTransactionSummary[];
}

export const MachineOverviewTab = ({ machine, slots, transactions }: MachineOverviewTabProps) => {
  const inventorySummary = slots.reduce(
    (acc, slot) => {
      acc.total += 1;
      if (slot.stock_level === 'empty') acc.empty += 1;
      else if (slot.stock_level === 'low') acc.low += 1;
      else if (slot.stock_level === 'good') acc.good += 1;
      return acc;
    },
    { total: 0, low: 0, empty: 0, good: 0 },
  );

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todaysVends = transactions.filter(
    (tx) => new Date(tx.vend_timestamp).getTime() >= todayStart.getTime(),
  );
  const todaysUnits = todaysVends.reduce((sum, tx) => sum + (tx.quantity || 0), 0);
  const recentActivity = [...transactions]
    .sort((a, b) => new Date(b.vend_timestamp).getTime() - new Date(a.vend_timestamp).getTime())
    .slice(0, 5);

  const alertAccent = machine.open_alerts_count > 0 ? COLORS.orange : COLORS.teal;

  return (
    <Box sx={{ overflowY: 'auto', pb: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        <KpiCard
          label="Inventory Health"
          value={machine.inventory_health_pct != null ? `${machine.inventory_health_pct}%` : '—'}
          sublabel={`${inventorySummary.good} healthy · ${inventorySummary.low} low · ${inventorySummary.empty} empty`}
          icon={<Package size={22} />}
        />
        <KpiCard
          label="Today's Vends"
          value={todaysUnits}
          sublabel={`${todaysVends.length} transaction${todaysVends.length === 1 ? '' : 's'} today`}
          icon={<ShoppingCart size={22} />}
        />
        <KpiCard
          label="Active Alerts"
          value={machine.open_alerts_count ?? 0}
          sublabel={machine.open_alerts_count > 0 ? 'Open alerts on this machine' : 'No open alerts'}
          icon={<AlertTriangle size={22} />}
          accent={alertAccent}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <VendingSectionCard>
          <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
            <VendingSectionTitle sx={{ mb: 1 }}>Inventory Summary</VendingSectionTitle>
            <VendingDetailRow>
              <VendingDetailLabel>Products</VendingDetailLabel>
              <VendingDetailValue>{inventorySummary.total}</VendingDetailValue>
            </VendingDetailRow>
            <VendingDetailRow>
              <VendingDetailLabel>Healthy</VendingDetailLabel>
              <VendingDetailValue $color={COLORS.success}>{inventorySummary.good}</VendingDetailValue>
            </VendingDetailRow>
            <VendingDetailRow>
              <VendingDetailLabel>Low</VendingDetailLabel>
              <VendingDetailValue $color={inventorySummary.low > 0 ? COLORS.orange : COLORS.darkGray}>
                {inventorySummary.low}
              </VendingDetailValue>
            </VendingDetailRow>
            <VendingDetailRow>
              <VendingDetailLabel>Empty</VendingDetailLabel>
              <VendingDetailValue $color={inventorySummary.empty > 0 ? COLORS.red : COLORS.darkGray}>
                {inventorySummary.empty}
              </VendingDetailValue>
            </VendingDetailRow>
          </CardContent>
        </VendingSectionCard>

        <VendingSectionCard>
          <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
            <VendingSectionTitle sx={{ mb: 1 }}>Machine Health</VendingSectionTitle>
            <VendingDetailRow>
              <VendingDetailLabel>Connection</VendingDetailLabel>
              <ConnectionStatusChip status={machine.connection_status} />
            </VendingDetailRow>
            <VendingDetailRow>
              <VendingDetailLabel>Status</VendingDetailLabel>
              <MachineStatusChip status={machine.status} />
            </VendingDetailRow>
            <VendingDetailRow>
              <VendingDetailLabel>Last Communication</VendingDetailLabel>
              <VendingDetailValue>{formatRelativeTime(machine.last_communication)}</VendingDetailValue>
            </VendingDetailRow>
            <VendingDetailRow>
              <VendingDetailLabel>Last Inventory Sync</VendingDetailLabel>
              <VendingDetailValue>{formatRelativeTime(machine.last_inventory_sync)}</VendingDetailValue>
            </VendingDetailRow>
            <VendingDetailRow>
              <VendingDetailLabel>Last Vend</VendingDetailLabel>
              <VendingDetailValue>{formatRelativeTime(machine.last_vend_at)}</VendingDetailValue>
            </VendingDetailRow>
            <VendingDetailRow>
              <VendingDetailLabel>Machine Errors</VendingDetailLabel>
              <VendingDetailValue $color={COLORS.success}>None</VendingDetailValue>
            </VendingDetailRow>
          </CardContent>
        </VendingSectionCard>
      </Box>

      <VendingSectionCard>
        <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
          <VendingSectionTitle>Recent Activity</VendingSectionTitle>
          {recentActivity.length === 0 && (
            <Typography variant="body2" sx={{ color: COLORS.lightGray, py: 1 }}>
              No recent vends recorded.
            </Typography>
          )}
          {recentActivity.map((tx) => (
            <ActivityItem key={tx.id}>
              <Typography variant="body2" sx={{ color: COLORS.lightGray, fontWeight: 600 }}>
                {formatActivityTime(tx.vend_timestamp)}
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.darkGray }}>
                {tx.product_name || 'Unknown product'}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
                {tx.location_key || '—'}
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.teal, textAlign: 'right' }}>
                Qty {tx.quantity}
              </Typography>
            </ActivityItem>
          ))}
        </CardContent>
      </VendingSectionCard>
    </Box>
  );
};

export default MachineOverviewTab;
